import {
  Page,
  Card,
  IndexTable,
  Badge,
  Text,
  BlockStack,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useBulkJobs } from '../hooks/useBulkJob';
import { PageEntrance } from '../components/ui';
import type { BulkJobDto } from '../types';
import { formatRelativeOrDate } from '../utils/dateFormat';
import './JobHistoryPage.css';

/**
 * T119 [US4]: JobHistoryPage — list past bulk jobs, download exports (FR-042).
 *
 * @see contracts/bulk.md, FR-042
 */
export default function JobHistoryPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useBulkJobs();

  const jobs = data?.jobs ?? [];

  if (!isLoading && jobs.length === 0) {
    return (
      <PageEntrance>
      <Page
        title="Job History"
        backAction={{ content: 'Bulk Operations', onAction: () => navigate('/bulk') }}
      >
        <Card>
          <BlockStack gap="400" align="center">
            <div className="z-empty">
              <div className="z-empty-icon">📋</div>
              <h3 className="z-empty-title">No bulk jobs yet</h3>
              <p className="z-empty-description">
                Start a bulk operation from the Bulk Operations page.
              </p>
            </div>
          </BlockStack>
        </Card>
      </Page>
      </PageEntrance>
    );
  }

  const rowMarkup = jobs.map((job: BulkJobDto, index: number) => (
    <IndexTable.Row
      id={job.id}
      key={job.id}
      position={index}
      onClick={() => navigate(`/bulk?jobId=${job.id}`)}
    >
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {job.jobType}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <JobStatusBadge status={job.status} />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {job.processedCount} / {job.totalProducts}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {job.successCount} ok / {job.failureCount} failed
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {formatRelativeOrDate(job.createdAt)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {job.completedAt
            ? formatRelativeOrDate(job.completedAt)
            : '—'}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <PageEntrance>
    <Page
      title="Job History"
      backAction={{ content: 'Bulk Operations', onAction: () => navigate('/bulk') }}
    >
      <Card>
        <IndexTable
          resourceName={{ singular: 'job', plural: 'jobs' }}
          itemCount={jobs.length}
          headings={[
            { title: 'Type' },
            { title: 'Status' },
            { title: 'Progress' },
            { title: 'Results' },
            { title: 'Created' },
            { title: 'Completed' },
          ]}
          selectable={false}
          loading={isLoading}
        >
          {rowMarkup}
        </IndexTable>
      </Card>
    </Page>
    </PageEntrance>
  );
}

function JobStatusBadge({ status }: Readonly<{ status: string }>) {
  switch (status) {
    case 'COMPLETED':
      return <Badge tone="success">Completed</Badge>;
    case 'RUNNING':
      return <Badge tone="attention">Running</Badge>;
    case 'QUEUED':
      return <Badge tone="info">Queued</Badge>;
    case 'FAILED':
      return <Badge tone="critical">Failed</Badge>;
    case 'CANCELLED':
      return <Badge>Cancelled</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}
